import React, { useEffect, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight, User, Calendar } from 'lucide-react';

const Lightbox = ({ photo, onClose, onPrev, onNext, hasMultiple }) => {
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') onClose();
    if (e.key === 'ArrowLeft' && hasMultiple) onPrev();
    if (e.key === 'ArrowRight' && hasMultiple) onNext();
  }, [onClose, onPrev, onNext, hasMultiple]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'auto';
    };
  }, [handleKeyDown]);

  const handleTouchStart = (e) => {
    const touch = e.touches[0];
    e.target.dataset.touchStartX = touch.clientX;
  };

  const handleTouchEnd = (e) => {
    if (!hasMultiple) return;
    const touchStartX = parseFloat(e.target.dataset.touchStartX);
    const touchEndX = e.changedTouches[0].clientX;
    const diff = touchStartX - touchEndX;

    if (Math.abs(diff) > 50) {
      if (diff > 0) onNext();
      else onPrev();
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { 
      day: 'numeric', 
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div 
      className="lightbox-overlay animate-fade-in"
      data-testid="lightbox"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      {/* Close Button */}
      <button
        data-testid="lightbox-close"
        onClick={onClose}
        className="absolute top-4 right-4 z-10 w-12 h-12 bg-black/50 rounded-full flex items-center justify-center text-white hover:bg-black/70 transition-colors"
      >
        <X className="w-6 h-6" />
      </button>

      {/* Navigation Buttons */}
      {hasMultiple && (
        <>
          <button
            data-testid="lightbox-prev"
            onClick={(e) => { e.stopPropagation(); onPrev(); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-black/50 rounded-full flex items-center justify-center text-white hover:bg-black/70 transition-colors"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            data-testid="lightbox-next"
            onClick={(e) => { e.stopPropagation(); onNext(); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-black/50 rounded-full flex items-center justify-center text-white hover:bg-black/70 transition-colors"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </>
      )}

      {/* Image */}
      <img
        src={photo.fullUrl}
        alt={photo.file_name}
        className="lightbox-image animate-scale-in"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      />

      {/* Photo Info */}
      <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
        <div className="max-w-md mx-auto text-white">
          <p className="font-medium truncate">{photo.file_name}</p>
          <div className="flex items-center gap-4 mt-2 text-sm text-white/80">
            <span className="flex items-center gap-1">
              <User className="w-4 h-4" />
              {photo.uploader_name}
            </span>
            <span className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {formatDate(photo.uploaded_at)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Lightbox;
